import { Link, useLocation, useNavigate } from 'react-router-dom'
import { FileCode, Database, Activity, FileText, Settings, LogOut, ChevronUp, ChevronDown, BarChart3 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ThemeToggle } from '@/components/ui/theme-toggle'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/store/authStore'
import { useTranslation } from 'react-i18next'
import { useState, useEffect } from 'react'

const navigation = [
  { name: 'Scripts', href: '/scripts', icon: FileCode, key: 'nav.scripts' },
  { name: 'Datasets', href: '/datasets', icon: Database, key: 'nav.datasets' },
  { name: 'Multi-Symbol', href: '/multi-symbol', icon: BarChart3, key: 'nav.multi-symbol' },
  { name: 'Jobs', href: '/jobs', icon: Activity, key: 'nav.jobs' },
  { name: 'Papers', href: '/papers', icon: FileText, key: 'nav.papers' },
]

export default function Header() {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const { user, logout } = useAuthStore()
  
  // Load collapsed state from localStorage
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('headerCollapsed')
      return saved === 'true'
    }
    return false
  })

  // Save collapsed state to localStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('headerCollapsed', String(collapsed))
    }
  }, [collapsed])

  const handleLogout = () => {
    logout()
    navigate('/auth/login')
  }

  const toggleCollapse = () => {
    setCollapsed(!collapsed)
  }

  return (
    <header className={cn("border-b bg-background transition-all duration-200", collapsed ? "h-10" : "h-16")}>
      <div className="container mx-auto px-4">
        <div className={cn("flex items-center justify-between transition-all duration-200", collapsed ? "h-10" : "h-16")}>
          <div className="flex items-center space-x-8">
            <Link to="/" className="flex items-center space-x-2">
              <span className={cn("font-bold text-primary transition-all duration-200", collapsed ? "text-lg" : "text-2xl")}>QuantQuack</span>
            </Link>
            {!collapsed && (
              <nav className="flex space-x-1">
                {navigation.map((item) => {
                  const Icon = item.icon
                  const isActive = location.pathname.startsWith(item.href)
                  return (
                    <Link key={item.name} to={item.href}>
                      <Button
                        variant={isActive ? "secondary" : "ghost"}
                        className={cn(
                          "flex items-center space-x-2",
                          isActive && "bg-accent"
                        )}
                      >
                        <Icon className="w-4 h-4" />
                        <span>{t(item.key)}</span>
                      </Button>
                    </Link>
                  )
                })}
              </nav>
            )}
          </div>
          <div className="flex items-center space-x-2">
            {!collapsed && (
              <>
                <ThemeToggle />
                <Link to="/settings">
                  <Button
                    variant={location.pathname === '/settings' ? "secondary" : "ghost"}
                    size="icon"
                  >
                    <Settings className="w-5 h-5" />
                  </Button>
                </Link>
                <div className="flex items-center gap-2">
                  {user && (
                    <span className="text-sm text-muted-foreground hidden md:inline">
                      {user.email}
                    </span>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleLogout}
                    title={t('auth.logout')}
                  >
                    <LogOut className="w-5 h-5" />
                  </Button>
                </div>
              </>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleCollapse}
              className="ml-2"
              title={collapsed ? "Expand navigation" : "Collapse navigation"}
            >
              {collapsed ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronUp className="w-4 h-4" />
              )}
            </Button>
          </div>
        </div>
      </div>
    </header>
  )
}
